import React, { useEffect, useState } from "react";
import styles from "./Rupture.module.sass";
import cn from "classnames";
import Card from "../../../../components/Card/index.js";
import Loader from "../../../../components/Loader/index.js";
import Actions from "../../../../components/Actions/index.js";
import { signRupture } from "../../../../services/signature.service.js";
import { isMinor } from "../../../../services/date.service.js";
import { downloadContract, deleteRupture } from "../../../../services/contract.service.js";
import { toast } from "react-toastify";

const Rupture = ({ className, contract, onContractUpdate, signatureStatus }) => {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signingInProgress, setSigningInProgress] = useState(false);

  /**
   * Gère le téléchargement du formulaire de rupture
   */
  const handleDownloadRupture = async () => {
    if (!contract || !contract.rupture) {
      toast.error("Aucun formulaire de rupture disponible pour ce contrat");
      return;
    }

    try {
      const success = await downloadContract({ ...contract, fileField: 'rupture' });
      if (success) {
        toast.success("Formulaire de rupture téléchargé avec succès!");
      } else {
        toast.error("Erreur lors du téléchargement du formulaire de rupture");
      }
    } catch (error) {
      toast.error(error.message || "Erreur lors du téléchargement du formulaire de rupture");
    }
  };

  /**
   * Gère la suppression du formulaire de rupture
   */
  const handleDeleteRupture = async () => {
    if (!contract || !contract.id) {
      toast.error("Impossible de supprimer la rupture : contrat invalide");
      return;
    }

    try {
      const updatedContract = await deleteRupture(contract.id);
      
      // Mettre à jour le contrat localement
      if (onContractUpdate) {
        onContractUpdate({
          ...contract,
          rupture: null,
          rupture_signature_status: "",
          procedureRupture: null,
          training_continued_after_break: null,
          rupture_reason: "",
          rupture_date: null,
          reason_for_break: "",
          date_for_break: null
        });
      }
      
      // Vider la liste des signatures
      setSignatures([]);
      
      toast.success("Formulaire de rupture supprimé avec succès!");
    } catch (error) {
      toast.error(error.message || "Erreur lors de la suppression du formulaire de rupture");
    }
  };

  const handleSignatureRequest = async () => {
    if (!contract || !contract.id) {
      toast.error("Impossible d'envoyer le document : contrat invalide");
      return;
    }

    if (!contract.rupture) {
      toast.error("Aucun formulaire de rupture disponible pour ce contrat");
      return;
    }

    try {
      setSigningInProgress(true);
      
      // Récupérer les données des signataires
      const student = contract.expand?.student_id || {};
      const company = contract.expand?.company_id || {};
      
      // Vérifier les emails
      const missingEmails = [];
      
      if (!student.email) missingEmails.push("l'étudiant");
      if (isMinor(student.birth_date) && !student.legal_guardian_email) missingEmails.push("le représentant légal");
      if (!company.director_email && !company.email) missingEmails.push("l'entreprise");
      
      if (missingEmails.length > 0) {
        toast.error(`Emails manquants pour ${missingEmails.join(', ')}`);
        setSigningInProgress(false);
        return;
      }
      
      // Envoyer le document à la signature
      const result = await signRupture(contract.id);
      
      // Mettre à jour le contrat localement avec l'ID de procédure
      if (onContractUpdate && result.procedure?.id) {
        onContractUpdate({
          ...contract,
          procedureRupture: result.procedure.id
        });
      }
      
      // Mettre à jour la liste des signatures
      generateSignaturesList();
      
      toast.success("Formulaire de rupture envoyé à la signature avec succès!");
    } catch (error) {
      toast.error(error.message || "Erreur lors de l'envoi du formulaire de rupture à la signature");
    } finally {
      setSigningInProgress(false);
    }
  };

  useEffect(() => {
    if (contract) {
      generateSignaturesList();
    }
  }, [contract, signatureStatus]);

  const generateSignaturesList = () => {
    if (!contract || !contract.expand) {
      setLoading(false);
      return;
    }

    // Récupérer les statuts de signature depuis YouSign (nouvelle structure v3)
    const memberStatuses = {};
    if (signatureStatus?.signers) {
      signatureStatus.signers.forEach(signer => {
        if (signer.email) {
          memberStatuses[signer.email.toLowerCase()] = {
            status: signer.status,
            finishedAt: signer.signed_at,
            firstName: signer.first_name,
            lastName: signer.last_name
          };
        }
      });
    }

    const student = contract.expand.student_id || {};
    const company = contract.expand.company_id || {};
    
    // Vérifier si l'étudiant est mineur
    const studentIsMinor = isMinor(student.birth_date);

    const companySignatory = {
      firstName: company.director_first_name || company.contact_first_name || '',
      lastName: company.director_last_name || company.contact_last_name || '',
      fullName: company.director_name || company.contact_name || 
                (company.director_first_name && company.director_last_name ? 
                `${company.director_first_name} ${company.director_last_name}` : 
                (company.contact_first_name && company.contact_last_name ? 
                `${company.contact_first_name} ${company.contact_last_name}` : 
                `Représentant de ${company.name || 'l\'entreprise'}`)),
      email: company.director_email || company.email || '',
      role: company.director_role || company.contact_role || 'Responsable'
    };
    
    // Récupérer les informations complètes des signataires
    const studentSignatory = {
      firstName: student.first_name || '',
      lastName: student.last_name || '',
      fullName: `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Étudiant',
      email: student.email || '',
      role: 'Apprenant'
    };
    
    const parentSignatory = studentIsMinor ? {
      firstName: student.legal_guardian_first_name || '',
      lastName: student.legal_guardian_last_name || '',
      fullName: student.legal_guardian_name || 
                (student.legal_guardian_first_name && student.legal_guardian_last_name ? 
                `${student.legal_guardian_first_name} ${student.legal_guardian_last_name}` : 
                'Parent/Tuteur légal'),
      email: student.legal_guardian_email || '',
      role: 'Représentant légal'
    } : null;
    
    // Créer la liste des signataires pour le formulaire de rupture
    const signaturesList = [];
    
    signaturesList.push({
      ordre: 1,
      role: `${companySignatory.role} - Entreprise`,
      signataire: companySignatory.fullName,
      email: companySignatory.email,
      statut: memberStatuses[companySignatory.email.toLowerCase()]?.status === 'signed' ? "Signé" :
              memberStatuses[companySignatory.email.toLowerCase()]?.status === 'declined' ? "Refusé" : 
              memberStatuses[companySignatory.email.toLowerCase()]?.status === 'notified' ? "Notifié" :
              memberStatuses[companySignatory.email.toLowerCase()]?.status === 'ready' ? "Prêt" : "En attente",
      date: memberStatuses[companySignatory.email.toLowerCase()]?.finishedAt ? 
            new Date(memberStatuses[companySignatory.email.toLowerCase()]?.finishedAt).toLocaleString('fr-FR', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            }) : ""
    });
  


    // 1. Étudiant (toujours en premier)
    signaturesList.push({
      ordre: 2,
      role: `${studentSignatory.role} - Étudiant`,
      signataire: studentSignatory.fullName,
      email: studentSignatory.email,
      statut: memberStatuses[studentSignatory.email.toLowerCase()]?.status === 'signed' ? "Signé" :
              memberStatuses[studentSignatory.email.toLowerCase()]?.status === 'declined' ? "Refusé" : 
              memberStatuses[studentSignatory.email.toLowerCase()]?.status === 'notified' ? "Notifié" :
              memberStatuses[studentSignatory.email.toLowerCase()]?.status === 'ready' ? "Prêt" : "En attente",
      date: memberStatuses[studentSignatory.email.toLowerCase()]?.finishedAt ? 
            new Date(memberStatuses[studentSignatory.email.toLowerCase()]?.finishedAt).toLocaleString('fr-FR', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            }) : ""
    });
    
    // 2. Parent légal (si mineur)
    if (studentIsMinor && parentSignatory) {
      signaturesList.push({
        ordre: 2,
        role: `${parentSignatory.role}`,
        signataire: parentSignatory.fullName,
        email: parentSignatory.email,
        statut: memberStatuses[parentSignatory.email.toLowerCase()]?.status === 'signed' ? "Signé" :
                memberStatuses[parentSignatory.email.toLowerCase()]?.status === 'declined' ? "Refusé" : 
                memberStatuses[parentSignatory.email.toLowerCase()]?.status === 'notified' ? "Notifié" :
                memberStatuses[parentSignatory.email.toLowerCase()]?.status === 'ready' ? "Prêt" : "En attente",
        date: memberStatuses[parentSignatory.email.toLowerCase()]?.finishedAt ? 
              new Date(memberStatuses[parentSignatory.email.toLowerCase()]?.finishedAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              }) : ""
      });
    }
    

    
    setSignatures(signaturesList);
    setLoading(false);
  };

  if (loading) {
    return (
      <Card
        className={cn(styles.card, className)}
        classCardHead={styles.head}
        title="Formulaire de rupture"
        classTitle={cn("title-green", styles.title)}
      >
        <div className={styles.wrapper}>
          <Loader />
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={cn(styles.card, className)}
      classCardHead={styles.head}
      title="Formulaire de rupture"
      classTitle={cn("title-yellow", styles.title)}
      head={
        <>
          {contract.rupture && (
            <Actions
              className={styles.actions}
              items={[
                ...(!contract.procedureRupture ? [{
                  title: signingInProgress ? "Envoi en cours..." : "Envoyer à la signature",
                  action: handleSignatureRequest,
                  disabled: signingInProgress
                }] : []),
                {
                  title: "Télécharger Rupture",
                  action: handleDownloadRupture
                },
                {
                  title: "Supprimer la rupture",
                  action: handleDeleteRupture
                }
              ]}
            />
          )}
        </>
      }
    >
      <div className={styles.wrapper}>
        {signatures.length > 0 ? (
          <div className={styles.table}>
            <div className={styles.row}>
              <div className={styles.col}>Ordre</div>
              <div className={styles.col}>Rôle</div>
              <div className={styles.col}>Signataire</div>
              <div className={styles.col}>Statut</div>
              <div className={styles.col}>Date</div>
            </div>
            {signatures.map((item, index) => (
              <div className={styles.row} key={index}>
                <div className={styles.col}>
                  {item.ordre}
                </div>
                <div className={styles.col}>
                  {item.role}
                </div>
                <div className={styles.col}>
                  <div className={styles.product}>{item.signataire}</div>
                  <div className={styles.invoice}>{item.email}</div>
                </div>
                <div className={styles.col}>
                  {item.statut === "Signé" && (
                    <div
                      className={cn(
                        "status-green-dark",
                        styles.status
                      )}
                    >
                      Signé
                    </div>
                  )}
                  {item.statut === "En attente" && (
                    <div
                      className={cn(
                        "status-yellow-dark",
                        styles.status
                      )}
                    >
                      En attente
                    </div>
                  )}
                  {item.statut === "Refusé" && (
                    <div
                      className={cn(
                        "status-red-dark",
                        styles.status
                      )}
                    >
                      Refusé
                    </div>
                  )}
                </div>
                <div className={styles.col}>
                  {item.date || "Non signé"}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            Aucune information de signature disponible
          </div>
        )}
      </div>
    </Card>
  );
};

export default Rupture;
